import {
  useSaveEvaluation,
  useSubmitEvaluation,
  useUnassignEvaluationTask,
  type SaveEvaluationRequest,
} from "@services/evaluationService";
import { toast } from "sonner";

/**
 * Hook to manage evaluation mutations
 * Wraps mutation hooks for easier consumption in components
 */
export function useEvaluationMutations() {
  const saveMutation = useSaveEvaluation();
  const submitMutation = useSubmitEvaluation();
  const unassignMutation = useUnassignEvaluationTask();

  const saveEvaluation = (
    assignmentId: string,
    data: SaveEvaluationRequest
  ) => {
    return saveMutation.mutateAsync(
      { assignmentId, data },
      {
        onError: () => {
          toast.error("Failed to save evaluation. Please try again.", {
            duration: 5 * 1000, // 5 seconds
          });
        },
      }
    );
  };

  const submitEvaluation = (assignmentId: string) => {
    return submitMutation.mutateAsync(assignmentId, {
      onError: () => {
        toast.error("Failed to submit evaluation. Please try again.", {
          duration: 5 * 1000, // 5 seconds
        });
      },
    });
  };

  const unassignTask = (assignmentId: string) => {
    return unassignMutation.mutateAsync(assignmentId, {
      onError: () => {
        toast.error("Failed to unassign task. Please try again.", {
          duration: 5 * 1000, // 5 seconds
        });
      },
    });
  };

  return {
    // Save evaluation
    saveEvaluation,
    isSaving: saveMutation.isPending,
    saveError: saveMutation.error,
    saveSuccess: saveMutation.isSuccess,

    // Submit evaluation
    submitEvaluation,
    isSubmitting: submitMutation.isPending,
    submitError: submitMutation.error,
    submitSuccess: submitMutation.isSuccess,

    // Unassign task
    unassignTask,
    isUnassigning: unassignMutation.isPending,
    unassignError: unassignMutation.error,
    unassignSuccess: unassignMutation.isSuccess,
  };
}
